import React, { useState, useMemo } from "react";
import { Confetti, playCorrect, playWrong } from "@/components/Bits";
import { DoneCard, RoundHeader } from "@/components/Cells";
import { shuffle } from "@/lib/quizgen";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

/* Tap numbers from smallest to biggest. rounds: [[3,4,5,6], ...] */
export default function OrderRunner({ title, category, modeId, build, backTo = "/learn" }) {
  const [seed, setSeed] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rounds = useMemo(() => build(seed).map(r => ({ sorted: r, pool: shuffle(r) })), [seed, build]);
  const [r, setR] = useState(0);
  const [filled, setFilled] = useState([]);
  const [shake, setShake] = useState(null);
  const [done, setDone] = useState(false);
  const round = rounds[r];

  const tap = (n) => {
    if (filled.includes(n)) return;
    const ok = n === round.sorted[filled.length];
    recordAnswer(category, ok);
    if (!ok) { playWrong(); setShake(n); setTimeout(() => setShake(null), 450); return; }
    playCorrect(String(n));
    const nf = [...filled, n]; setFilled(nf);
    if (nf.length === round.sorted.length) {
      setTimeout(() => {
        if (r + 1 >= rounds.length) { completeActivity(category, modeId); setDone(true); speak("Perfect order!"); }
        else { setR(r + 1); setFilled([]); }
      }, 700);
    }
  };
  const again = () => { setSeed(s => s + 1); setR(0); setFilled([]); setDone(false); };

  if (done) return <DoneCard title="Perfect order!" emoji="📶" onAgain={again} backTo={backTo} subtitle={`You finished ${title}!`} />;

  return (
    <div className="ll-bg min-h-screen pb-32">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <RoundHeader idx={r} total={rounds.length} label={`Round ${r + 1} / ${rounds.length}`} />
        <div className="ll-card p-5 sm:p-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-purple-900">Tap from smallest to biggest</h2>
          <div className="flex justify-center gap-2 sm:gap-3 mt-6" data-testid="order-slots">
            {round.sorted.map((_, i) => (
              <div key={i} className={`w-14 h-16 sm:w-20 sm:h-24 rounded-2xl border-4 flex items-center justify-center font-display font-bold text-3xl sm:text-5xl ${filled[i] != null ? "bg-emerald-50 border-emerald-300 text-emerald-700 ll-pop" : "border-dashed border-purple-300 bg-white/60 text-purple-300"}`}>
                {filled[i] ?? "?"}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {round.pool.map(n => (
              <button key={n} data-testid={`order-num-${n}`} onClick={() => tap(n)} disabled={filled.includes(n)}
                className={`ll-tile bg-white w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center font-display font-bold text-4xl sm:text-5xl text-purple-700 ${filled.includes(n) ? "opacity-30" : ""} ${shake === n ? "ll-shake ring-4 ring-pink-300" : ""}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Confetti show={filled.length === round.sorted.length} />
    </div>
  );
}
